import React from 'react';
import PropTypes from 'prop-types';
import { Descriptions, Divider, Typography, Statistic } from 'antd';
import ProCard from '@ant-design/pro-card';
import {
  CurrencyDisplay,
  DateDisplay,
  ProTable,
  FileList,
} from 'components/layout';
import { ENUM_APPLICATION_TEAM_MEMBER_TYPES } from 'constants/form';
import dateDiffInMonthsAndDays from 'utils/dateDiffInMonthsAndDays';
import './ApplicationDetail.less';

const { Text } = Typography;

const descriptionsColumn = {
  xs: 1,
  sm: 1,
  md: 2,
};
const columns = [
  {
    title: 'Team member type',
    dataIndex: 'teamMemberType',
    key: 'teamMemberType',
    valueEnum: ENUM_APPLICATION_TEAM_MEMBER_TYPES,
  },
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
  },
  {
    title: 'Department',
    dataIndex: ['department', 'name'],
    key: 'department',
  },
  {
    title: 'Position',
    dataIndex: 'position',
    key: 'position',
  },
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'email',
  },
  {
    title: 'NetID',
    dataIndex: 'netId',
    key: 'netId',
  },
];
const ApplicationDetail = ({ data, loading }) => {
  return (
    <ProCard
      title="Application Detail"
      style={{ margin: '24px 0' }}
      loading={loading}
      bordered
      collapsible={true}
    >
      <Descriptions column={1}>
        <Descriptions.Item
          label="Funding"
          className="ant-descriptions-item-bold"
        >
          {data?.setting?.title}
        </Descriptions.Item>
        <Descriptions.Item label="Application submission date">
          <DateDisplay date={data?.firstSubmissionDate} />
        </Descriptions.Item>
        <Descriptions.Item label="Application Period">
          <DateDisplay date={data?.setting?.startDate} />
          {` - `}
          <DateDisplay date={data?.setting?.endDate} />
        </Descriptions.Item>
        <Descriptions.Item label="Deadline for Using Allocated Funds">
          <DateDisplay date={data?.setting?.deadlineOfUsingUpFunding} />
        </Descriptions.Item>
      </Descriptions>
      <Divider />
      <Descriptions column={descriptionsColumn}>
        <Descriptions.Item
          label="Project title"
          span={2}
          className="ant-descriptions-item-bold"
        >
          {data?.title}
        </Descriptions.Item>
        <Descriptions.Item label="Application ref. number" span={2}>
          {data?.refNumber ?? 'N/A'}
        </Descriptions.Item>
        <Descriptions.Item
          label="Is the project research-oriented or with research elements?"
          span={2}
        >
          {data?.isResearchOriented ? 'Yes' : 'No'}
        </Descriptions.Item>
        <Descriptions.Item label="Proposal" span={2}>
          <FileList files={data?.proposal ? [data?.proposal] : []} />
        </Descriptions.Item>
      </Descriptions>
      <Divider />
      <ProTable
        columns={columns}
        dataSource={data?.team}
        options={false}
        search={false}
        pagination={false}
        rowKey="id"
        tableRender={(_props, _dom, { table }) => table}
        title={() => <Text strong>Particulars of the applicants</Text>}
      ></ProTable>
      <Divider />
      <Descriptions column={1}>
        <Descriptions.Item>
          <div style={{ margin: '16px 0' }}>
            <Statistic
              title="Total funding requested"
              value={data?.tdgFund + data?.matchingFund}
              prefix="$"
              precision={2}
            />
          </div>
        </Descriptions.Item>
        <Descriptions.Item label="From TDG">
          <CurrencyDisplay amount={data?.tdgFund ?? 0} />
        </Descriptions.Item>
        <Descriptions.Item label="From matching fund">
          <CurrencyDisplay amount={data?.matchingFund ?? 0} />
        </Descriptions.Item>
        <Descriptions.Item label="Matching fund source" span={2}>
          {data?.matchingFundFundingSource}
        </Descriptions.Item>
      </Descriptions>
      <Divider />
      <Descriptions column={descriptionsColumn}>
        <Descriptions.Item span={2}>
          <div style={{ margin: '16px 0' }}>
            <Statistic
              title="Expected duration of project"
              value={dateDiffInMonthsAndDays(data?.startDate, data?.endDate)}
              formatter=""
            />
          </div>
        </Descriptions.Item>
        <Descriptions.Item label="Proposed commencement date">
          <DateDisplay date={data?.startDate} />
        </Descriptions.Item>
        <Descriptions.Item label="Expected completion date">
          <DateDisplay date={data?.endDate} />
        </Descriptions.Item>
      </Descriptions>
    </ProCard>
  );
};

ApplicationDetail.defaultProps = {
  data: null,
  loading: true,
};

ApplicationDetail.propTypes = {
  data: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
};

export default ApplicationDetail;
